import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Table from "src/componets/Table";
import { useAppDispatch, useAppSelector } from "src/store/hooks";
import { ColumnsType } from "antd/es/table";
import { OrderInterface } from "src/store/callrecorder/ordersSlice";
import { fetchOrders, reprocessOrder, reprocessBundleOrders } from "src/actions/callrecorder/ordersActions";
import { queryParams } from "src/helpers/UrlHelpers";
import { formattedDate } from "src/helpers/DateHelpers";
import usePermissions from "src/hooks/usePermissions";
import Pagination from "src/componets/Pagination";
import OptionalLink from "src/componets/OptionalLink";
import callrecorderUrls from "src/configs/callrecorder/callrecorderUrls";

import { Button, Popconfirm, Space, Tag, Tooltip, message as antdMessage } from "antd";
import { RedoOutlined } from "@ant-design/icons";
import { Button as ButtonLink } from "src/componets/Buttons";

type ReprocessStatus = 'pending' | 'failed';

interface ListTableInterface {
  filterParams: object;
  limit?: number;
  // When set, bulk "Reprocess pending/failed" actions are shown for this bundle.
  bundleId?: number | string;
}

const _ListTable = ({ filterParams, limit = 25, bundleId }: ListTableInterface) => {
  const dispatch = useAppDispatch();
  const [reprocessingIds, setReprocessingIds] = useState<number[]>([]);
  const [bulkStatus, setBulkStatus] = useState<ReprocessStatus | null>(null);
  const [searchParams] = useSearchParams();
  const currentPage = queryParams.getInt(searchParams, 'page') || 1;
  const ids = useAppSelector(state => state.callrecorder.orders.ids);
  const entities = useAppSelector(state => state.callrecorder.orders.entities);
  const count = useAppSelector(state => state.callrecorder.orders.count);
  const { write_callrecorder } = usePermissions();
  const data = ids ? ids.map(id => entities[id]) : [];

  const urlOrderBy = searchParams.get('order_by') || '';
  const urlOrder = searchParams.get('order') || '';

  const params = useMemo(() => ({
    limit,
    offset: (currentPage - 1) * limit,
    ...filterParams,
    ...(urlOrderBy ? { order_by: urlOrderBy, order: urlOrder } : {})
  }), [limit, currentPage, filterParams, urlOrderBy, urlOrder]);

  useEffect(() => {
    dispatch(fetchOrders(params));
  }, [params, dispatch]);

  async function onReprocess(id: number) {
    if (reprocessingIds.includes(id)) return;

    setReprocessingIds(ids => [...ids, id]);
    try {
      const payload = await dispatch(reprocessOrder(id)).unwrap();
      if (payload.meta?.success) {
        antdMessage.success(`Order #${id} reprocessed and completed`);
      } else {
        antdMessage.error(`Order #${id}: ${(payload.meta?.errors || []).join(', ') || 'reprocess failed'}`);
      }
    } catch (e: any) {
      antdMessage.error(`Order #${id}: ${e?.message || 'reprocess failed'}`);
    } finally {
      setReprocessingIds(ids => ids.filter(i => i !== id));
    }
  }

  async function onReprocessAll(status: ReprocessStatus) {
    if (!bundleId) return;

    setBulkStatus(status);
    try {
      const { queued_count } = await dispatch(reprocessBundleOrders({ bundle_id: bundleId, status })).unwrap();
      antdMessage.success(`Queued ${queued_count} ${status} order(s) for reprocessing`);
      dispatch(fetchOrders(params));
    } catch (e: any) {
      antdMessage.error(e?.message || 'Failed to queue reprocessing');
    } finally {
      setBulkStatus(null);
    }
  }

  const columns: ColumnsType<OrderInterface> = [
    {
      key: 'id',
      title: 'ID',
      render: (_, { id }) => <small>{`#${id}`}</small>,
      width: 60
    },

    {
      key: 'user_id',
      title: 'User',
      render: (_, { user_id }) => user_id ? (
        <OptionalLink link={callrecorderUrls.userPath(user_id)}>
          <small>{`#${user_id}`}</small>
        </OptionalLink>
      ) : '—',
      width: 60
    },

    {
      key: 'status',
      title: 'Status',
      render: (_, { status }) => {
        const color = status === 'completed' ? 'success'
          : status === 'pending' ? 'processing'
          : status === 'failed' ? 'error'
          : 'default';
        return <Tag color={color}>{status}</Tag>;
      },
      width: 90
    },

    {
      key: 'amount',
      title: 'Amount',
      render: (_, { amount }) => amount ? <small>{amount}</small> : '—',
      width: 80
    },

    {
      key: 'price',
      title: 'Price',
      render: (_, { price }) => price ? <small>{price}</small> : '—',
      width: 80
    },

    {
      key: 'productid',
      title: 'Product ID',
      render: (_, { productid }) => productid ? <small>{productid}</small> : '—',
      width: 120
    },

    {
      key: 'transaction_id',
      title: 'Transaction',
      render: (_, { transaction_id }) => <small>{transaction_id}</small>,
      width: 150
    },

    {
      key: 'code',
      title: 'Error Code',
      render: (_, { code, message }) => {
        const codeText = code !== null ? code : '—';
        if (!message) return codeText;
        return (
          <Tooltip title={message}>
            <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>{codeText}</span>
          </Tooltip>
        );
      },
      width: 50
    },

    {
      key: 'created_at',
      title: 'Created',
      render: (_, { created_at }) => <small>{formattedDate(created_at).dateTime}</small>,
      width: 125
    },

    ...(write_callrecorder ? [{
      key: 'action',
      title: 'Action',
      render: (_: any, { id, status }: OrderInterface) => status === 'completed' ? null : (
        <Space size="middle">
          <Popconfirm title={`Reprocess order #${id}?`} onConfirm={() => onReprocess(id)}>
            <ButtonLink
              onClick={() => {}}
              title={reprocessingIds.includes(id) ? 'Reprocessing…' : 'Reprocess'}
              icon={RedoOutlined}
            />
          </Popconfirm>
        </Space>
      ),
      width: 50
    }] : []),
  ];

  return (
    <div>
      {
        bundleId && write_callrecorder &&
          <div className='v-mb-10' style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <Popconfirm
              title='Reprocess all pending orders for this bundle?'
              onConfirm={() => onReprocessAll('pending')}
            >
              <Button size='small' icon={<RedoOutlined />} loading={bulkStatus === 'pending'}>
                Reprocess pending
              </Button>
            </Popconfirm>

            <Popconfirm
              title='Reprocess all failed orders for this bundle?'
              onConfirm={() => onReprocessAll('failed')}
            >
              <Button size='small' danger icon={<RedoOutlined />} loading={bulkStatus === 'failed'}>
                Reprocess failed
              </Button>
            </Popconfirm>
          </div>
      }

      <Table
        rowKey='id'
        columns={columns}
        dataSource={data}
        sort_keys={['id', 'user_id', 'amount', 'price', 'created_at']}
      />

      <Pagination className='v-mt-10' total={count} size={limit} />
    </div>
  )
}

export default _ListTable;
